import type { IconButtonProps } from '@mui/material/IconButton';

import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import MenuItem from '@mui/material/MenuItem';
import MenuList from '@mui/material/MenuList';
import Typography from '@mui/material/Typography';

import { useRouter, usePathname } from 'src/routes/hooks';
import { paths } from 'src/routes/paths';

import { usePopover, CustomPopover } from 'src/components/custom-popover';
import { Label } from 'src/components/label';

import { useAuthContext } from 'src/auth/hooks';

import { AccountButton } from './account-button';
import { SignOutButton } from './sign-out-button';
import { useTranslation } from 'react-i18next';
import { useUserPermissions } from 'src/hooks/use-user-permissions';
import { PermissionKeys } from 'src/auth/types';

// ----------------------------------------------------------------------

export type AccountPopoverProps = IconButtonProps & {
  data?: {
    label: string;
    href: string;
    icon?: React.ReactNode;
    info?: React.ReactNode;
  }[];
};

export function AccountPopover({ data = [], sx, ...other }: AccountPopoverProps) {
  const router = useRouter();

  const popover = usePopover();

  const pathname = usePathname();

  const { user } = useAuthContext();

  const { t } = useTranslation('user');

  const {hasAnyPermission} = useUserPermissions();

  const menuItems = [
    {
      label: t('account.account'),
      href: paths.dashboard.account,
      icon: '',
      info: '',
    },
    hasAnyPermission([{permission: PermissionKeys.VIEW_MEMBER_PROFILE_PAGE, levels: ['OWN']}]) && {
      label: t('account.memberProfile'),
      href: paths.dashboard.member.profile,
      icon: '',
      info: '',
    },
  ].filter(Boolean) as { label: string; href: string; icon: string; info: string }[];

  const handleClickItem = (path: string) => {
    popover.onClose();
    router.push(path);
  };

  return (
    <>
      <AccountButton
        open={popover.open}
        onClick={popover.onOpen}
        photoURL={user?.client?.imageURL || ''}
        displayName={user?.name || ''}
        sx={sx}
        {...other}
      />

      <CustomPopover
        open={popover.open}
        anchorEl={popover.anchorEl}
        onClose={popover.onClose}
        slotProps={{
          paper: { sx: { p: 0, width: 200 } },
          arrow: { offset: 20 },
        }}
      >
        <Box sx={{ p: 2, pb: 1.5 }}>
          <Typography variant="subtitle2" noWrap>
            {user?.name}
          </Typography>

          <Typography variant="body2" sx={{ color: 'text.secondary' }} noWrap>
            {user?.inspector?.email}
          </Typography>
        </Box>

        {menuItems.length > 0 && (
          <>
            <Divider sx={{ borderStyle: 'dashed' }} />
            <MenuList sx={{ p: 1, my: 1 }}>
              {menuItems.map((option) => {
                const rootLabel = pathname.includes('/dashboard') ? t('common:navigation.home') : t('common:navigation.dashboard');

                const rootHref = pathname.includes('/dashboard') ? '/' : paths.dashboard.root;

                return (
                  <MenuItem
                    key={option?.label}
                    onClick={() =>
                      handleClickItem(option?.label === 'Home' ? rootHref : option?.href)
                    }
                    sx={{
                      py: 1,
                      color: 'text.secondary',
                      '& svg': { width: 24, height: 24 },
                      '&:hover': { color: 'text.primary' },
                    }}
                  >
                    {option?.icon}

                    <Box component="span">
                      {option?.label === 'Home' ? rootLabel : option?.label}
                    </Box>

                    {option?.info && (
                      <Label color="error" sx={{ ml: 1 }}>
                        {option?.info}
                      </Label>
                    )}
                  </MenuItem>
                );
              })}
            </MenuList>
          </>
        )}

        <Divider sx={{ borderStyle: 'dashed' }} />

        <Box sx={{ p: 1 }}>
          <SignOutButton
            size="medium"
            variant="text"
            onClose={popover.onClose}
            sx={{ display: 'block', textAlign: 'left' }}
          />
        </Box>
      </CustomPopover>
    </>
  );
}
